import React, { useState, useEffect, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Slider as AntSlider } from "antd";

import { displayTextWidth } from "./slider.util.js";
import "antd/es/slider/style/css";
import { FieldLabel, FieldHint, FieldErrors } from "../fields/index.jsx";
import { Slider as ReadOnlySlider } from "./slider.readonly.js";

import styles from "./slider.css";

export const SLIDER_SIZE = {
  NORMAL: "normal",
  SMALL: "small"
};

/**
 * Sliders reflect a range of values along a bar, from which users may select a single value
 */

export function Slider({
  value,
  onChange,
  min,
  max,
  defaultValue,
  intervalSize,
  readOnly,
  disabled,
  fromDatasheet,
  trackAutoWidth = true,
  className,
  getTooltipContainer,
  tooltipPrefixCls = "",
  size = SLIDER_SIZE.NORMAL,
  hideToolTip,
  label,
  hint,
  errors,
  required,
  id,
  isForm,
  readOnlyTooltipOnHover = false,
  markDisabled = false,
  labelLeftIcon,
  labelRightIcon
}) {
  let [marks, setMarks] = useState({
    [min]: min,
    [max]: max
  });
  let [sliderValue, setSliderValue] = useState(defaultValue);
  let [margins, setMargins] = useState({
    left: "0px",
    right: "0px"
  });

  const sliderRef = useRef(null);

  let tooltipContainer = null;

  if (getTooltipContainer) {
    tooltipContainer = getTooltipContainer;
  } else {
    tooltipContainer = (trigger) => {
      return sliderRef.current || trigger.parentElement;
    };
  }

  useEffect(
    function setMarginAndMarks() {
      if (trackAutoWidth) {
        let labels = {};
        labels[min] = min.toString();
        labels[max] = max.toString();
        let marginLeft =
          Math.ceil(displayTextWidth(min.toString(), "1.2rem Inter") / 2) + 10;
        let marginRight =
          Math.ceil(displayTextWidth(max.toString(), "1.2rem Inter") / 2) + 10;
        setMargins({
          left: marginLeft + "px",
          right: marginRight + "px"
        });
        setMarks(labels);
      }
    },
    [min, max, trackAutoWidth]
  );

  useEffect(
    function renderValue() {
      if (value !== null && value !== undefined) {
        setSliderValue(value);
      }
    },
    [value]
  );

  const onValueChange = (value) => {
    if (value !== null && value !== undefined) {
      setSliderValue(value);
    }
  };

  const readOnlySliderProps = {};
  if (!readOnlyTooltipOnHover && readOnly) {
    readOnlySliderProps.tooltipVisible = true;
  }

  if (hideToolTip) {
    readOnlySliderProps.tooltipVisible = false;
  }

  return (
    <React.Fragment>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          targetId={id}
          disabled={disabled}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
          size={size}
        />
      )}
      {readOnly ? (
        <ReadOnlySlider
          value={value || defaultValue}
          min={min}
          max={max}
          readOnlyTooltipOnHover={readOnlyTooltipOnHover}
          getTooltipPopupContainer={tooltipContainer}
          isForm={isForm}
        />
      ) : (
        <div
          className={`${fromDatasheet ? styles.gridSlider : ""}
        ${styles.sliderContainer}
        ${readOnly ? styles.containerReadOnly : ""}
        ${className}`}
          style={{
            marginInlineStart: margins.left,
            marginInlineEnd: margins.right
          }}
          ref={sliderRef}
          data-component="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={sliderValue}
        >
          <AntSlider
            min={min}
            max={max}
            value={sliderValue}
            disabled={disabled || readOnly}
            step={intervalSize}
            onChange={onValueChange}
            onAfterChange={onChange}
            marks={markDisabled ? [] : marks}
            tooltipPrefixCls={`${tooltipPrefixCls} slider-tooltip ${
              readOnly ? "slider-readonly-tooltip" : ""
            } ant-tooltip`}
            className={`${styles.slider} 
              ${styles[size]}
              ${disabled ? styles.disabled : ""}
              ${readOnly ? styles.readOnly : ""}
            `}
            getTooltipPopupContainer={tooltipContainer}
            aria-label={id}
            {...readOnlySliderProps}
          />
        </div>
      )}
      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </React.Fragment>
  );
}

Slider.propTypes = {
  /**
   * Value of slider
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Additional classname for slider component
   */
  className: PropTypes.string,
  /**
   * Event handler for onChange event
   */
  onChange: PropTypes.func,
  /**
   * Determines if the Slider is readOnly
   */
  readOnly: PropTypes.bool,
  /**
   * Determines if the Slider is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The minimum value the slider can slide to
   */
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * The maximum value the slider can slide to
   */
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Determines whether to automatically set margins
   */
  trackAutoWidth: PropTypes.bool,
  /**
   * The granularity the slider can step through values.
   * Must greater than 0, and be divided by (max - min) . When marks no null, step can be null.
   */
  intervalSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Default value of the slider
   */
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Determines whether component is used in datasheet
   */
  fromDatasheet: PropTypes.bool,
  /**
   * Determines whether to disable tooltip
   */
  hideToolTip: PropTypes.bool,
  /**
   * The DOM container of the Tooltip, the default behavior is to create a div element in body.
   */
  getTooltipContainer: PropTypes.func,
  /**
   * Specifies slider size.
   */
  size: PropTypes.string,
  /**
   * Target id for field label, field hint, field error
   */
  id: PropTypes.string,
  /**
   * Specifies field label.
   */
  label: PropTypes.string,
  /**
   * Specifies field hint.
   */
  hint: PropTypes.string,
  /**
   * Errors to show in field error.
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Specifies whether the field is required.
   */
  required: PropTypes.bool,
  /**
   * Event handler for onHover event of readOnlySlider
   */
  readOnlyTooltipOnHover: PropTypes.bool,
  tooltipPrefixCls: PropTypes.string,
  /**
   * Specifies whether it is from form
   */
  isForm: PropTypes.bool,
  /**
   * To declare slider marks as empty
   */
  markDisabled: PropTypes.bool,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node
};

Slider.defaultProps = {
  onChange: () => {},
  trackAutoWidth: true,
  tooltipPrefixCls: "",
  size: SLIDER_SIZE.NORMAL,
  readOnlyTooltipOnHover: false,
  markDisabled: false
};
